import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2 } from "lucide-react";
import api, { formatApiError, postSSE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function TeacherPortal() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [grades, setGrades] = useState([]);
  const [taskOpen, setTaskOpen] = useState(false);
  const [gradeOpen, setGradeOpen] = useState(false);
  const [task, setTask] = useState({ title: "", description: "", subject_id: "", course: "", section: "A", due_date: "" });
  const [grade, setGrade] = useState({ student_id: "", subject_id: "", period: "P1", evaluation: "", score: "", published: true, comment: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiProvider, setAiProvider] = useState("openai");

  const load = useCallback(() => {
    api.get("/maestro/resumen").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/r/grades").then((r) => setGrades(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-slate-400 text-sm">Cargando tu portal…</p>;

  const subjectName = (sid) => data.subjects.find((s) => s.id === sid)?.name || "—";
  const studentName = (sid) => {
    const s = data.students.find((x) => x.id === sid);
    return s ? `${s.first_name} ${s.last_name}` : "—";
  };

  const submitTask = async () => {
    try {
      await api.post("/r/assignments", task);
      toast.success("Tarea publicada");
      setTaskOpen(false);
      setTask({ title: "", description: "", subject_id: "", course: "", section: "A", due_date: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitGrade = async () => {
    try {
      const st = data.students.find((s) => s.id === grade.student_id);
      await api.post("/r/grades", { ...grade, score: parseFloat(grade.score), course: st?.grade || "" });
      toast.success("Calificación publicada");
      setGradeOpen(false);
      setGrade({ student_id: "", subject_id: "", period: "P1", evaluation: "", score: "", published: true, comment: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generateComment = async () => {
    if (!grade.student_id || !grade.subject_id) {
      toast.error("Selecciona estudiante y materia primero");
      return;
    }
    setAiBusy(true);
    setGrade((g) => ({ ...g, comment: "" }));
    try {
      await postSSE("/ai/grade-comment", {
        student_id: grade.student_id, subject_id: grade.subject_id, period: grade.period, provider: aiProvider,
      }, {
        onDelta: (d) => setGrade((g) => ({ ...g, comment: (g.comment || "") + d })),
        onError: (msg) => toast.error(msg),
      });
    } catch (e) {
      toast.error(e.message || "Error de IA");
    } finally {
      setAiBusy(false);
    }
  };

  const deleteTask = async (t) => {
    try {
      await api.delete(`/r/assignments/${t.id}`);
      toast.success("Tarea eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="teacher-portal">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="teacher-portal-title">Portal del Maestro — {user?.name}</h1>
        <p className="text-sm text-slate-500 mt-1">Solo ves las materias y cursos que tienes asignados.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {data.subjects.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`subject-card-${s.id}`}>
            <p className="font-semibold text-slate-900">{s.name}</p>
            <p className="text-xs text-slate-500 mt-1">{s.grade}</p>
          </div>
        ))}
        {data.subjects.length === 0 && <p className="text-slate-400 text-sm col-span-full">No tienes materias asignadas. Contacta al administrador.</p>}
      </div>

      <Tabs defaultValue="tareas">
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          <TabsTrigger value="tareas" data-testid="ttab-tareas">Tareas</TabsTrigger>
          <TabsTrigger value="notas" data-testid="ttab-notas">Calificaciones</TabsTrigger>
          <TabsTrigger value="estudiantes" data-testid="ttab-estudiantes">Estudiantes</TabsTrigger>
        </TabsList>

        <TabsContent value="tareas">
          <div className="flex justify-end mb-3">
            <Button data-testid="create-task-button" className="bg-blue-800 hover:bg-blue-700" onClick={() => setTaskOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Nueva tarea
            </Button>
          </div>
          <DataTable testid="teacher-tasks-table" rows={data.assignments} columns={[
            { key: "title", label: "Tarea" },
            { key: "subject_id", label: "Materia", render: (t) => subjectName(t.subject_id) },
            { key: "course", label: "Curso" },
            { key: "section", label: "Sección" },
            { key: "due_date", label: "Entrega" },
            {
              key: "_a", label: "",
              render: (t) => t.created_by === user?.id ? (
                <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`delete-task-${t.id}`} onClick={() => deleteTask(t)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              ) : null,
            },
          ]} />
        </TabsContent>

        <TabsContent value="notas">
          <div className="flex justify-end mb-3">
            <Button data-testid="create-grade-button" className="bg-blue-800 hover:bg-blue-700" onClick={() => setGradeOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Publicar nota
            </Button>
          </div>
          <DataTable testid="teacher-grades-table" rows={grades} columns={[
            { key: "student_id", label: "Estudiante", render: (g) => studentName(g.student_id) },
            { key: "subject_id", label: "Materia", render: (g) => subjectName(g.subject_id) },
            { key: "evaluation", label: "Evaluación" },
            { key: "period", label: "Período" },
            { key: "score", label: "Nota", render: (g) => <span className="font-mono font-semibold">{g.score}</span> },
            { key: "comment", label: "Comentario", render: (g) => <span className="text-xs text-slate-500 line-clamp-2 max-w-xs block">{g.comment || "—"}</span> },
            { key: "published", label: "Publicada", render: (g) => (g.published ? "Sí" : "No") },
          ]} />
        </TabsContent>

        <TabsContent value="estudiantes">
          <DataTable testid="teacher-students-table" rows={data.students} columns={[
            { key: "name", label: "Estudiante", render: (s) => `${s.first_name} ${s.last_name}` },
            { key: "grade", label: "Curso" },
            { key: "section", label: "Sección" },
          ]} />
        </TabsContent>
      </Tabs>

      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nueva tarea</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Título</Label><Input data-testid="task-title" value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Descripción</Label><Textarea data-testid="task-description" value={task.description} onChange={(e) => setTask({ ...task, description: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Materia</Label>
              <Select value={task.subject_id} onValueChange={(v) => setTask({ ...task, subject_id: v })}>
                <SelectTrigger data-testid="task-subject"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{data.subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Curso</Label>
                <Select value={task.course} onValueChange={(v) => setTask({ ...task, course: v })}>
                  <SelectTrigger data-testid="task-course"><SelectValue placeholder="Curso" /></SelectTrigger>
                  <SelectContent>{data.courses.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sección</Label>
                <Select value={task.section} onValueChange={(v) => setTask({ ...task, section: v })}>
                  <SelectTrigger data-testid="task-section"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Entrega</Label><Input type="date" data-testid="task-due-date" value={task.due_date} onChange={(e) => setTask({ ...task, due_date: e.target.value })} /></div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-task" onClick={() => setTaskOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-task" className="bg-blue-800 hover:bg-blue-700" onClick={submitTask}>Publicar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={gradeOpen} onOpenChange={setGradeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Publicar calificación</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Estudiante</Label>
              <Select value={grade.student_id} onValueChange={(v) => setGrade({ ...grade, student_id: v })}>
                <SelectTrigger data-testid="grade-student"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{data.students.map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.grade}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Materia</Label>
              <Select value={grade.subject_id} onValueChange={(v) => setGrade({ ...grade, subject_id: v })}>
                <SelectTrigger data-testid="grade-subject"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{data.subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Período</Label>
                <Select value={grade.period} onValueChange={(v) => setGrade({ ...grade, period: v })}>
                  <SelectTrigger data-testid="grade-period"><SelectValue /></SelectTrigger>
                  <SelectContent>{["P1", "P2", "P3", "P4"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Nota (0-100)</Label><Input type="number" data-testid="grade-score" value={grade.score} onChange={(e) => setGrade({ ...grade, score: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Evaluación</Label><Input data-testid="grade-evaluation" placeholder="Ej: Examen parcial" value={grade.evaluation} onChange={(e) => setGrade({ ...grade, evaluation: e.target.value })} /></div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Comentario para la boleta</Label>
                <div className="flex items-center gap-1.5">
                  <Select value={aiProvider} onValueChange={setAiProvider}>
                    <SelectTrigger className="h-7 w-[104px] text-xs" data-testid="grade-ai-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">ChatGPT</SelectItem>
                      <SelectItem value="anthropic">Claude</SelectItem>
                      <SelectItem value="gemini">Gemini</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="grade-ai-generate" disabled={aiBusy} onClick={generateComment}>
                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                    Generar con IA
                  </Button>
                </div>
              </div>
              <Textarea rows={3} data-testid="grade-comment" placeholder="Comentario del período (puedes generarlo con IA y editarlo)" value={grade.comment} onChange={(e) => setGrade({ ...grade, comment: e.target.value })} />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="grade-published" checked={grade.published} onCheckedChange={(v) => setGrade({ ...grade, published: v })} />
              <Label className="!mt-0">Visible para el padre</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-grade" onClick={() => setGradeOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-grade" className="bg-blue-800 hover:bg-blue-700" onClick={submitGrade}>Publicar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
